import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Server, Terminal as TerminalIcon, X, Columns2, Rows2, Pencil, RadioTower, Save, SquareX, LayoutGrid } from 'lucide-react';
import Tooltip from '../ui/Tooltip';
import ContextMenu from '../ui/ContextMenu';
import InlineNameEditor from '../ui/InlineNameEditor';
import { useTerminalExtStore, tabPanes } from '../../store/terminalExtStore';
import { PaneIcon } from './TerminalPaneHeader';
import { useSplitWithMenu } from './SplitWithMenu';

/** The AI tab badge. Styled inline because `index.css` is a hand-rolled
 * utility subset, not Tailwind — an invented utility class would silently do
 * nothing. */
const AI_BADGE = {
  fontSize: 9,
  fontWeight: 700,
  letterSpacing: 0.3,
  padding: '0 3px',
  borderRadius: 3,
  border: '1px solid currentColor',
  opacity: 0.7,
};

/**
 * One tab in the strip (Collections `etm-tab` look). Icon = the first pane's
 * (agent lettermark, SSH, or terminal); a pane-count badge when split; a
 * broadcast mark when broadcasting. Double-click the label to rename;
 * right-click for the tab menu.
 */
export default function TerminalTabItem({ tab, active, onSelect, onClose, onSaveLayout }) {
  const { t } = useTranslation();
  const allPanes = useTerminalExtStore((s) => s.panes);
  const panes = useMemo(() => tabPanes({ panes: allPanes }, tab), [allPanes, tab]);
  const [renaming, setRenaming] = useState(false);
  const [menu, setMenu] = useState(null);
  const first = panes[0];
  const count = panes.length;
  const st = useTerminalExtStore;
  const { openSplitWith, splitWithMenu } = useSplitWithMenu();

  const items = [
    !tab.locked && { icon: <Columns2 size={14} />, label: t('terminalExt.splitRightPick', 'Split right…'), onClick: () => openSplitWith(tab.focusedPaneId, 'row', menu), disabled: !st.getState().canSplitPane(tab.focusedPaneId, 'row').ok },
    !tab.locked && { icon: <Rows2 size={14} />, label: t('terminalExt.splitDownPick', 'Split down…'), onClick: () => openSplitWith(tab.focusedPaneId, 'col', menu), disabled: !st.getState().canSplitPane(tab.focusedPaneId, 'col').ok },
    { icon: <Pencil size={14} />, label: t('terminalExt.renameTab', 'Rename'), onClick: () => setRenaming(true) },
    !tab.locked && onSaveLayout && { icon: <Save size={14} />, label: t('terminalExt.saveLayout', 'Save layout…'), onClick: () => onSaveLayout(tab.id) },
    !tab.locked && count > 1 && {
      icon: <RadioTower size={14} />,
      label: tab.broadcast ? t('terminalExt.broadcastStop', 'Stop broadcasting input') : t('terminalExt.broadcast', 'Broadcast input to all panes'),
      onClick: () => st.getState().setBroadcast(tab.id, !tab.broadcast),
    },
    count > 1 && { icon: <SquareX size={14} />, label: t('terminalExt.closeOtherPanes', 'Close other panes'), onClick: () => st.getState().closeOtherPanes(tab.focusedPaneId) },
    { icon: <X size={14} />, label: t('terminalExt.closeTab', 'Close tab'), onClick: () => onClose(tab.id), danger: true },
  ];

  let icon;
  if (first?.icon) icon = <PaneIcon pane={first} />;
  else if (first?.kind === 'ssh') icon = <Server size={13} className="flex-shrink-0 text-emerald-600" aria-label="SSH" />;
  else icon = <TerminalIcon size={13} className="flex-shrink-0 text-gray-500" aria-label="Terminal" />;

  return (
    <div
      className={`flex items-center space-x-2 px-2 py-1 min-w-0 flex-shrink-0 cursor-pointer transition-colors duration-150 etm-tab ${
        active ? 'etm-tab--active' : 'etm-tab--inactive'
      }`}
      style={{ maxWidth: 200, overflow: 'hidden' }}
      data-tab-active={active ? 'true' : undefined}
      onClick={() => onSelect(tab.id)}
      onContextMenu={(e) => {
        e.preventDefault();
        setMenu({ x: e.clientX, y: e.clientY });
      }}
    >
      {icon}
      {/* The AI tab is not the user's shell: approved commands and Ctrl-C only. */}
      {first?.ai && (
        <span className="flex-shrink-0" aria-label="AI" style={AI_BADGE}>
          AI
        </span>
      )}
      {renaming ? (
        <span className="term-tab-rename" onClick={(e) => e.stopPropagation()}>
          <InlineNameEditor
            value={tab.label}
            onSave={(v) => {
              st.getState().renameTab(tab.id, v);
              setRenaming(false);
            }}
            onCancel={() => setRenaming(false)}
          />
        </span>
      ) : (
        // Truncated text: the one place a native title stays (gotcha rule).
        <span
          className="text-xs font-medium truncate min-w-0 flex-1"
          title={tab.label}
          onDoubleClick={(e) => {
            e.stopPropagation();
            setRenaming(true);
          }}
        >
          {tab.label}
        </span>
      )}
      {count > 1 && (
        <span className="term-tab-badge" aria-label={t('terminalExt.paneCount', '{{count}} panes', { count })}>
          <LayoutGrid size={10} aria-hidden />
          {count}
        </span>
      )}
      {tab.broadcast && (
        <span className="term-tab-badge term-tab-badge--broadcast" aria-label={t('terminalExt.broadcastOn', 'Broadcast input: on')}>
          <RadioTower size={11} aria-hidden />
        </span>
      )}
      <Tooltip content={t('common.close', 'Close')} side="bottom">
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onClose(tab.id);
          }}
          className="tab-bar-close-btn flex-shrink-0 cursor-pointer rounded-lg bg-transparent py-1 transition-colors"
          aria-label={t('common.close', 'Close')}
        >
          <X size={11} />
        </button>
      </Tooltip>
      {menu && <ContextMenu x={menu.x} y={menu.y} items={items} onClose={() => setMenu(null)} />}
      {splitWithMenu}
    </div>
  );
}
